/**
 * PWA install detection — pure logic. The browser side of the wizard
 * lives in `useInstallState`; this module just answers two questions
 * given a user-agent string:
 *
 *   - Which platform are we on (Android Chrome, iOS Safari, etc.)?
 *   - What's the install strategy (native prompt, manual steps, or
 *     unsupported entirely)?
 *
 * Detection is best-effort. UA sniffing isn't reliable enough to gate
 * critical paths but is good enough for "show the right wizard panel".
 * The native-prompt path doesn't depend on this — `beforeinstallprompt`
 * either fires or it doesn't.
 */

export type InstallPlatform =
  | "android-chrome"
  | "android-other"
  | "ios-safari"
  | "ipad-safari"
  | "desktop-chrome"
  | "desktop-edge"
  | "desktop-safari"
  | "desktop-firefox"
  | "unknown";

export type InstallStrategy =
  /** `beforeinstallprompt` will fire — show the native install button. */
  | "native"
  /** No prompt API, but the OS supports PWA install via menu (iOS Safari,
   *  iPadOS Safari) — show illustrated step-by-step instructions. */
  | "manual"
  /** Browser doesn't support PWA install at all — show a "use a supported
   *  browser" message with a link to the docs. */
  | "unsupported";

export interface InstallEnv {
  /** `navigator.userAgent` (or whatever stub is being passed in tests). */
  userAgent: string;
  /** Result of `matchMedia('(display-mode: standalone)')`. iOS sets
   *  `navigator.standalone = true` instead — caller can OR them. */
  standalone: boolean;
  /** True when iOS Safari `navigator.standalone` is true. Caller passes
   *  it explicitly because we can't read iOS-specific properties from a
   *  pure module. */
  iosStandalone: boolean;
}

/**
 * Heuristic platform detection. Order matters — UA strings often contain
 * multiple browser tokens and we want the "owning" one. iPadOS reports
 * a Mac UA in Safari 13+, so we rely on `maxTouchPoints` (caller passes
 * a hint or we fall back to the UA `iPad` token).
 */
export function detectPlatform(env: InstallEnv): InstallPlatform {
  const ua = env.userAgent.toLowerCase();

  // iOS / iPadOS — must be detected before generic "safari" since iOS
  // Safari's UA includes both. iPadOS 13+ reports as Mac, but typical
  // builds still include `ipad` in the UA when not in desktop-mode.
  if (/ipad|iphone|ipod/.test(ua)) {
    if (/ipad/.test(ua)) return "ipad-safari";
    return "ios-safari";
  }

  // Android Chrome (and Chrome derivatives that fire beforeinstallprompt).
  if (/android/.test(ua)) {
    if (/chrome|crios|samsungbrowser|edg/.test(ua)) return "android-chrome";
    return "android-other";
  }

  // Desktop Edge — must come before Chrome since the Edge UA includes
  // both `edg` and `chrome` tokens.
  if (/edg\//.test(ua)) return "desktop-edge";
  if (/firefox/.test(ua)) return "desktop-firefox";
  // Desktop Safari (no Chrome/Chromium in the UA).
  if (/safari/.test(ua) && !/chrome|chromium/.test(ua)) {
    return "desktop-safari";
  }
  if (/chrome|chromium/.test(ua)) return "desktop-chrome";

  return "unknown";
}

/** Whether the app is currently running as an installed PWA. */
export function isStandalone(env: InstallEnv): boolean {
  return env.standalone || env.iosStandalone;
}

/**
 * Pick the install UX for a platform. iOS Safari and iPadOS Safari are
 * the only `manual` cases — every other supported browser fires
 * `beforeinstallprompt`. Android-other (Firefox / Samsung old) and
 * Desktop Firefox / Safari are `unsupported`.
 */
export function installStrategy(p: InstallPlatform): InstallStrategy {
  switch (p) {
    case "android-chrome":
    case "desktop-chrome":
    case "desktop-edge":
      return "native";
    case "ios-safari":
    case "ipad-safari":
      return "manual";
    case "desktop-safari":
    case "desktop-firefox":
    case "android-other":
    case "unknown":
      return "unsupported";
  }
}

/** Friendly label per platform for the wizard heading. */
export function platformLabel(p: InstallPlatform): string {
  switch (p) {
    case "android-chrome":
      return "Android · Chrome";
    case "android-other":
      return "Android";
    case "ios-safari":
      return "iPhone · Safari";
    case "ipad-safari":
      return "iPad · Safari";
    case "desktop-chrome":
      return "Ordenador · Chrome";
    case "desktop-edge":
      return "Ordenador · Edge";
    case "desktop-safari":
      return "Mac · Safari";
    case "desktop-firefox":
      return "Ordenador · Firefox";
    case "unknown":
      return "Tu navegador";
  }
}
